import { supabase } from "@/integrations/supabase/client";
import { isBotTraffic, isHumanVerified } from "@/lib/botDetection";

// ============================================================
// Captura first-party de visitas + atribuicao de origem
// (UTM / gclid / fbclid -> canal organico, pago, social, etc.)
// Grava em public.page_views. Nunca quebra a navegacao.
// ============================================================

const SESSION_KEY = "ao_session";
const ATTR_KEY = "ao_attr";
const FIRST_TOUCH_KEY = "ao_attr_first";

export type Channel = "paid" | "organic" | "social" | "referral" | "direct";

export interface Attribution {
  referrer: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  channel: Channel;
  session_id: string;
}

type RawAttr = Omit<Attribution, "channel" | "session_id">;

function uuid(): string {
  try {
    return crypto.randomUUID();
  } catch {
    return `s-${Date.now()}-${Math.floor(Math.random() * 1e9).toString(16)}`;
  }
}

function detectDevice(): string {
  const w = window.innerWidth;
  if (w < 640) return "mobile";
  if (w < 1024) return "tablet";
  return "desktop";
}

const PAID_MEDIUMS = ["cpc", "ppc", "paid", "paidsearch", "paid_search", "cpm", "display", "paid_social", "paidsocial"];
const SOCIAL_SOURCES = ["facebook", "instagram", "fb", "ig", "meta", "tiktok", "linkedin", "youtube", "twitter", "pinterest", "whatsapp"];
const SEARCH_REFERRERS = ["google.", "bing.", "yahoo.", "duckduckgo.", "ecosia.", "yandex.", "search.brave"];
const SOCIAL_REFERRERS = ["facebook.", "l.facebook", "lm.facebook", "instagram.", "t.co", "twitter.", "x.com", "linkedin.", "youtube.", "tiktok.", "pinterest.", "wa.me", "whatsapp"];

function classifyChannel(a: RawAttr): Channel {
  const src = (a.utm_source || "").toLowerCase();
  const med = (a.utm_medium || "").toLowerCase();
  const ref = (a.referrer || "").toLowerCase();

  // Sinais explicitos de anuncio pago
  if (a.gclid) return "paid";
  if (PAID_MEDIUMS.includes(med)) return "paid";
  if (a.fbclid) return PAID_MEDIUMS.includes(med) ? "paid" : "social";

  // UTM explicitos
  if (med === "social" || SOCIAL_SOURCES.some(s => src.includes(s))) return "social";
  if (med === "organic") return "organic";
  if (med === "referral" || med === "email") return "referral";
  if (src) return "referral"; // tem utm_source mas sem medium claro

  // Sem UTM: deduz pelo referrer
  if (!ref) return "direct";
  if (SEARCH_REFERRERS.some(s => ref.includes(s))) return "organic";
  if (SOCIAL_REFERRERS.some(s => ref.includes(s))) return "social";
  if (ref.includes("atacadooptico")) return "direct"; // navegacao interna
  return "referral";
}

function getSessionId(): string {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = uuid();
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

/**
 * Atribuicao da SESSAO: capturada uma vez na entrada (referrer + UTM da URL)
 * e reaplicada em todas as paginas da mesma sessao.
 */
function buildAttribution(): Attribution {
  const cached = sessionStorage.getItem(ATTR_KEY);
  if (cached) {
    try {
      return JSON.parse(cached) as Attribution;
    } catch {
      /* recomputa abaixo */
    }
  }

  const params = new URLSearchParams(window.location.search);
  const get = (k: string) => params.get(k) || null;
  const raw: RawAttr = {
    referrer: document.referrer || null,
    utm_source: get("utm_source"),
    utm_medium: get("utm_medium"),
    utm_campaign: get("utm_campaign"),
    utm_term: get("utm_term"),
    utm_content: get("utm_content"),
    gclid: get("gclid"),
    fbclid: get("fbclid"),
  };
  const attr: Attribution = { ...raw, channel: classifyChannel(raw), session_id: getSessionId() };

  sessionStorage.setItem(ATTR_KEY, JSON.stringify(attr));
  // Primeiro toque (persistente) para atribuir conversoes futuras
  if (!localStorage.getItem(FIRST_TOUCH_KEY)) {
    localStorage.setItem(FIRST_TOUCH_KEY, JSON.stringify(attr));
  }
  return attr;
}

/** Atribuicao da sessao atual (para anexar em leads/orcamentos). */
export function getStoredAttribution(): Attribution | null {
  const c = sessionStorage.getItem(ATTR_KEY);
  if (c) {
    try {
      return JSON.parse(c) as Attribution;
    } catch {
      return null;
    }
  }
  return null;
}

/**
 * Atribuicao de AQUISICAO: prioriza o primeiro toque (a campanha que trouxe
 * o visitante pela primeira vez), com fallback para a sessao atual.
 * Use ao registrar uma conversao (cadastro de lojista).
 */
export function getAcquisitionAttribution(): Attribution | null {
  const first = localStorage.getItem(FIRST_TOUCH_KEY);
  if (first) {
    try {
      return JSON.parse(first) as Attribution;
    } catch {
      /* fallback abaixo */
    }
  }
  return getStoredAttribution() || buildAttribution();
}

/** Dispara um page_view no Google Analytics 4 (SPA). Silencioso se o gtag nao carregou. */
export function gtagPageView(path: string): void {
  try {
    if (isBotTraffic() || !isHumanVerified()) return;
    const w = window as unknown as { gtag?: (...args: unknown[]) => void };
    if (typeof w.gtag === "function") {
      w.gtag("event", "page_view", {
        page_path: path,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  } catch {
    /* nao quebra a navegacao */
  }
}

/** Registra uma visualizacao de pagina. Silencioso em caso de erro. */
export async function trackPageView(path: string): Promise<void> {
  try {
    if (typeof window === "undefined") return;
    if (isBotTraffic() || !isHumanVerified()) return;
    const attr = buildAttribution();
    const isAdmin = path.startsWith("/admin") || path.startsWith("/lojista");
    const row = {
      path,
      referrer: attr.referrer,
      utm_source: attr.utm_source,
      utm_medium: attr.utm_medium,
      utm_campaign: attr.utm_campaign,
      utm_term: attr.utm_term,
      utm_content: attr.utm_content,
      gclid: attr.gclid,
      fbclid: attr.fbclid,
      channel: attr.channel,
      session_id: attr.session_id,
      is_admin: isAdmin,
      device: detectDevice(),
      user_agent: navigator.userAgent ? navigator.userAgent.slice(0, 300) : null,
    };
    // page_views ainda nao esta nos tipos gerados; cast ate o Lovable regenerar.
    await (supabase as unknown as { from: (t: string) => { insert: (r: unknown) => Promise<unknown> } })
      .from("page_views")
      .insert(row);
  } catch {
    // Tracking jamais deve interromper a navegacao do usuario.
  }
}
